import * as React from 'react';

import { translate } from '~l10n/client';

import { Action } from '~shared/components/ActionMenu/ActionMenu';
import { IconRestore } from '~shared/components/icons/standard';
import { toast } from '~shared/components/Toast';
import {
  isOverFolderLimit,
  isOverQuizLimit,
} from '~teacher/components/workspaces/utilities/checkWorkspaceItemLimit';
import { groupWorkspaceItemsByType } from '~teacher/components/workspaces/utilities/groupWorkspaceItemsByType';
import { MAX_WORKSPACE_ITEMS } from '~teacher/components/workspaces/WorkspaceQuizzes/actions/constants';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useRestoreWorkspaceFolder } from '~teacher/hook/workspaces/useRestoreWorkspaceFolder';
import { useRestoreWorkspaceQuizzes } from '~teacher/hook/workspaces/useRestoreWorkspaceQuizzes';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

interface UseRestoreWorkspaceItemActionParams {
  workspaceId: string;
  paywallCallback: () => void;
}

export function useRestoreWorkspaceItemAction({
  workspaceId,
  paywallCallback,
}: UseRestoreWorkspaceItemActionParams): Action<WorkspaceItem> {
  const {
    permissions: {
      restoreQuiz: canRestoreQuiz,
      restoreWorkspaceFolder: canRestoreWorkspaceFolder,
    },
  } = useWorkspacePermissionsV2({ workspaceId });
  const restoreWorkspaceFolder = useRestoreWorkspaceFolder();
  const restoreWorkspaceQuiz = useRestoreWorkspaceQuizzes();
  return React.useMemo(
    () => ({
      action: ({ data, close }) => {
        const { folder, quiz } = groupWorkspaceItemsByType(data);

        if (isOverFolderLimit(folder) || isOverQuizLimit(quiz)) {
          toast.critical(
            translate('Please select less than {0} items').format(
              MAX_WORKSPACE_ITEMS
            )
          );
          close();
          return;
        }

        const workspaceFolderIds = (folder ?? []).map(
          (workspaceFolder) => workspaceFolder.id
        );
        const workspaceQuizIds = (quiz ?? []).map(
          (workspaceQuiz) => workspaceQuiz.id
        );

        if (workspaceQuizIds.length > MAX_WORKSPACE_ITEMS) {
          toast.critical(
            translate('Please select less than {0} items').format(
              MAX_WORKSPACE_ITEMS
            )
          );
        } else if (workspaceQuizIds.length > 0) {
          restoreWorkspaceQuiz.mutate(
            { workspaceId, workspaceQuizIds },
            {
              onSuccess: () => {
                if (workspaceQuizIds.length > 1) {
                  toast.positive(
                    translate('{0} items restored to Library').format(
                      workspaceQuizIds.length
                    )
                  );
                  return;
                }
                toast.positive(translate('1 item restored to Library'));
              },
              onError: () => {
                toast.critical(translate('An error has occurred'));
              },
            }
          );
        }

        if (workspaceFolderIds.length > 0) {
          restoreWorkspaceFolder.mutate(
            { workspaceId, workspaceFolderIds },
            {
              onError: () => {
                toast.critical(translate('An error has occurred'));
              },
            }
          );
        }

        close();
      },
      icon: <IconRestore />,
      key: 'restore-item',
      title: translate('Restore'),
      disabled: ({ data }) => {
        const { quiz, folder } = groupWorkspaceItemsByType(data);
        return (
          (quiz.length >= 1 && !canRestoreQuiz) ||
          (folder.length >= 1 && !canRestoreWorkspaceFolder)
        );
      },
      paywallCallback:
        !canRestoreQuiz && !canRestoreWorkspaceFolder ? paywallCallback : null,
      showProBadge: !canRestoreQuiz && !canRestoreWorkspaceFolder,
    }),
    [
      canRestoreQuiz,
      canRestoreWorkspaceFolder,
      paywallCallback,
      restoreWorkspaceFolder,
      restoreWorkspaceQuiz,
      workspaceId,
    ]
  );
}
